import { DndContext, DragOverlay, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent, type DragOverEvent, type DragStartEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { pictureName, useAnimatedAssetIds } from './animated-image';
import { stripHtmlForTooltip } from './formula-plaintext';
import { OrderingItemMediaContent, type OrderingItemMedia } from './ordering-item-media';
import { PreviewHtml } from './variable-chip-preview';

// Prevents a NARROWER, separate animation glitch from `@dnd-kit/sortable`: whenever a tile's
// INDEX within the sortable items array changes (e.g. after a commit reorders `order`), the
// library derives a one-off transform between its rect just before and just after that change,
// including a scaleX/scaleY term. `animateLayoutChanges: () => false` disables that path entirely.
// This is NOT what fixes the "tile grows to match the size of whatever it's hovering over" bug —
// see the DragOverlay usage below for that (a genuinely separate mechanism, in `@dnd-kit/core`
// itself, unconditionally recomputing scale from `over.rect` on every active drag frame).
const disableAnimateLayoutChanges = () => false;

/** How much of a NEIGHBOUR's name a tile speaks before trimming. Matches the 40 chars
 * `formula-plaintext` already uses to keep a long label from swamping a chip. */
const NEIGHBOUR_NAME_MAX = 40;

export interface OrderingCircleItem {
  id: string;
  text: string; // rich text HTML, same convention as the canonical item list
  media?: OrderingItemMedia;
  /** When set, rendered INSTEAD of `text` — the caller's escape hatch for content that isn't
   * plain rich-text HTML (e.g. an equations-mode item's formula, shown as a real styled variable
   * chip via `FormulaWithValues`, matching the standing "variables always render as a bracketed
   * chip in a display context" rule the vertical/horizontal preview already follows). `text` still
   * has to be provided either way (used for drag announcements/plainLabel elsewhere), this is
   * purely a visual override. */
  formulaChip?: React.ReactNode;
}

export type OrderingCircleDirection = 'clockwise' | 'counterclockwise';

interface OrderingCircleProps {
  /** Current order, index 0 = 12-o'clock starting slot (which rotational direction the index
   * then advances in is `direction` below, not fixed). */
  items: OrderingCircleItem[];
  /** Non-interactive preview mode (isPreview's "what a student encounters" simulation) — no
   * drag/keyboard handlers attached at all, matching every other block's disabled-preview
   * convention (e.g. slider's disabled SliderTrack). */
  disabled?: boolean;
  /** Called with the new item-id order once a drag or keyboard move commits. Omitted entirely
   * in disabled/preview mode. */
  onChange?: (nextOrder: string[]) => void;
  /** Baseline container diameter in px — a floor, not a fixed value. The actual rendered ring
   * grows past this for larger item counts so tiles don't overlap (see minRadiusForCount). */
  size?: number;
  /** Which way increasing index advances around the ring — a pure rendering/arrangement choice
   * (see OrderingBlockData.circularDirection's own doc comment for why this never touches
   * grading). Defaults clockwise, matching every ring before this setting existed. */
  direction?: OrderingCircleDirection;
}

// Rebuilt on @dnd-kit (SortableContext/useSortable, the same primitives the vertical/horizontal
// shapes already use) rather than the previous fully hand-rolled pointer-angle-drag gesture —
// dnd-kit has no built-in circular layout, so each tile's REST position is still computed via the
// same trig this component always used (slotAngle/slotPosition), applied as a static left/top
// offset; dnd-kit's own `transform` (the live drag-follow delta) is layered on top of that via a
// second `translate()` in the same `transform` string. Collision detection (`closestCenter`) reads
// each tile's actual rendered/measured rect regardless of how that position was computed, so a
// circular arrangement "just works" with the same collision algorithm the linear shapes use — no
// custom circular collision logic needed. Only the drag GESTURE moves to dnd-kit; keyboard
// interaction is deliberately NOT routed through dnd-kit's own KeyboardSensor (whose coordinate
// getter assumes list/grid geometry, not slot-index rotation) — it stays a direct per-tile
// onKeyDown, calling the exact same commitMove reorder+announce path a completed drag uses, so
// the two input methods can never drift into different behavior.
// 140px, widened from an original 108 and DELIBERATELY kept at 140: at 108 only ~70px was left for
// text after the grip icon and padding — about 8-10 characters, "just a keyword" per direct
// feedback. Briefly trimmed to 128 while shrinking the ring, then restored on request: legibility
// wins over footprint here.
//
// Width is still the dominant term in the ring's horizontal size (it sets rx almost entirely), so
// this is the lever to reach for if the ring ever needs to be narrower — but note it does NOT
// affect height, which the ellipse below governs independently. The oversized-ring problem this
// component had was vertical, and that's fixed without touching width.
const TILE_WIDTH = 140;
// Tiles aren't rotated to face outward (they stay axis-aligned regardless of ring position, same
// as the old round-pill version) and a media tile can render noticeably taller than a plain-text
// one (an image is capped at 64px via OrderingItemMediaContent's `constrained` mode, plus padding/
// border) — this is the conservative worst-case height used for the ANTI-OVERLAP calculation
// below, not a real fixed height (each tile's actual rendered height still varies with content).
const TILE_HEIGHT_MEDIA = 80;
// A text tile is clamped to 2 lines (see the tile render), so ~2x20px of text plus padding/border.
// Kept separate from the media figure rather than collapsing both into one worst case: assuming
// every ring might hold an image forced text-only rings — the common case — to reserve vertical
// room nothing in them can occupy.
const TILE_HEIGHT_TEXT = 56;
const TILE_GAP = 6;
// Bounding-circle radius around a tile's own center — if two tiles' bounding circles don't
// overlap, the tiles themselves (rectangles inside those circles) can't overlap either, regardless
// of the angle between them. A conservative, cheap-to-compute stand-in for exact rectangle-vs-
// rectangle collision math, which would otherwise need to vary per pair of angles.
// Tiles are WIDE and SHORT (128x56), so they need roughly twice as much horizontal separation as
// vertical. A circle reserves the same distance in every direction, which means every bit of the
// vertical allowance beyond what a 56px tile needs is wasted space — visible as big gaps between
// the top and bottom tiles while the left/right ones sit correctly. An ellipse spends the room where
// the tiles actually need it. Width is unaffected: horizontal separation is the binding constraint,
// so rx lands on the same value either way; only the wasted height goes.
const ELLIPSE_RATIO = 0.7;

/** Axis-aligned rectangles clear each other when they're separated on EITHER axis — so this is the
 * real test, replacing the circumscribed-circle estimate that stood in for it before. That estimate
 * reserved each tile's full DIAGONAL in every direction, which is what made the ring bigger than it
 * needed to be even before the ellipse. */
function slotsClear(rx: number, ry: number, n: number, tileHeight: number): boolean {
  const pts = Array.from({ length: n }, (_, i) => ringPointAt(i, n, rx, ry, 'clockwise'));
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const dx = Math.abs(pts[i]!.x - pts[j]!.x);
      const dy = Math.abs(pts[i]!.y - pts[j]!.y);
      if (dx < TILE_WIDTH + TILE_GAP && dy < tileHeight + TILE_GAP) return false;
    }
  }
  return true;
}

/** The smallest ring radius at which `n` evenly-spaced tiles' bounding circles stop overlapping —
 * grows with `n` (more items packed onto the same ring means less arc length between neighbors),
 * so unlike the old round-pill version (a fixed-size ring regardless of item count, safe only
 * because pills were small/uniform), this component's ring must grow for larger item counts or
 * tiles would visually overlap. Chord length between adjacent slots on a ring of radius r is
 * `2r·sin(π/n)`; solving for r against the minimum non-overlapping chord gives this. */
/** Smallest rx at which no two tiles overlap, found by scanning upward. Sound because scaling the
 * whole arrangement grows every |dx| and |dy| together, so the first clearing value is the minimum —
 * and cached, since the answer only depends on (n, tileHeight) and would otherwise recompute on
 * every render. A closed form for the either-axis condition on an ellipse isn't worth the fragility
 * at these item counts (MAX_CIRCULAR_ITEMS keeps n small). */
const minRxCache = new Map<string, number>();
function minRadiusForCount(n: number, tileHeight: number): number {
  if (n < 2) return 0;
  const key = `${n}:${tileHeight}`;
  const cached = minRxCache.get(key);
  if (cached !== undefined) return cached;
  let rx = 20;
  while (rx < 900 && !slotsClear(rx, rx * ELLIPSE_RATIO, n, tileHeight)) rx += 1;
  minRxCache.set(key, rx);
  return rx;
}

function isRtl(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
}

/** theta=0 is 12-o'clock (screen "up") — increasing index moves clockwise from there BEFORE the
 * `direction` mirror below is applied; ringPointAt is where that mirror actually happens. */
function slotAngle(index: number, n: number): number {
  return (index * (2 * Math.PI)) / n;
}

// Fraction of the angular gap between two adjacent slots left un-arced on each side — keeps an
// arc's endpoints from running under the tiles themselves (which sit AT the slot angle, not
// beside it), so what's visible reads as "arcing from the gap next to one tile toward the gap
// next to the next," not a line drawn straight through both tiles' centers.
const ARC_TRIM_FRACTION = 0.22;

/** A point on the ring at a continuous (possibly fractional) slot position `t` — an integer `t`
 * gives a real tile's own position; a fractional one is used for arc endpoints (see
 * ringArcPath), and `t === n` is coterminal with `t === 0` (sin/cos are 2π-periodic), which is
 * what lets the "wrap from the last slot back to the first" arc fall out for free.
 *
 * `direction === 'counterclockwise'` mirrors the x coordinate — reflecting a shape across a
 * vertical axis reverses its rotational sense, so the SAME increasing-t formula that sweeps
 * clockwise in the default orientation sweeps counterclockwise once mirrored, while index 0 stays
 * exactly at 12-o'clock either way (x = 0 there, unaffected by the mirror). This is a pure
 * rendering/arrangement choice (see OrderingBlockData.circularDirection's own doc comment) —
 * nothing about grading depends on which screen direction the index advances in. */
function ringPointAt(t: number, n: number, rx: number, ry: number, direction: OrderingCircleDirection): { x: number; y: number } {
  const theta = slotAngle(t, n) - Math.PI / 2;
  const mirror = direction === 'counterclockwise' ? -1 : 1;
  return { x: mirror * rx * Math.cos(theta), y: ry * Math.sin(theta) };
}

function slotPosition(index: number, n: number, rx: number, ry: number, direction: OrderingCircleDirection): { x: number; y: number } {
  return ringPointAt(index, n, rx, ry, direction);
}

/** SVG path `d` for one arced connector running from just past slot `fromIndex` to just before
 * slot `fromIndex + 1` (mod n, via ringPointAt's own wrap-for-free trick) — an arc of the ring
 * itself (same radius as the tiles' own centers), not a straight chord, so it visually reads as
 * "following the ring" the way the user asked for ("connect the objects with arced arrows between
 * them along the circle"). `cx`/`cy` are the ring container's own center in the same pixel space
 * the tiles are positioned in (half the container's diameter). */
function ringArcPath(fromIndex: number, n: number, rx: number, ry: number, cx: number, cy: number, direction: OrderingCircleDirection): string {
  const start = ringPointAt(fromIndex + ARC_TRIM_FRACTION, n, rx, ry, direction);
  const end = ringPointAt(fromIndex + 1 - ARC_TRIM_FRACTION, n, rx, ry, direction);
  // A mirror reverses orientation, so the sweep direction that traces the SHORT way between two
  // adjacent (mirrored) slot points flips too — sweep=1 (clockwise, SVG's positive-angle
  // direction in a y-down coordinate system) for the default orientation, sweep=0 once mirrored.
  // A single-slot arc never approaches a half-circle for any realistic item count, so the
  // large-arc-flag is always 0.
  const sweepFlag = direction === 'clockwise' ? 1 : 0;
  // SVG's arc command already takes rx and ry separately, so an elliptical ring needs no new
  // path maths — the connectors follow the same curve the tiles sit on for free.
  return `M ${cx + start.x} ${cy + start.y} A ${rx} ${ry} 0 0 ${sweepFlag} ${cx + end.x} ${cy + end.y}`;
}

/** The arced-arrow connectors between adjacent tiles, drawn as one continuous loop (including the
 * wrap from the last item back to the first — circular ordering has no fixed "end," any rotation
 * of the correct sequence is equally correct, so the ring's own connective loop should look
 * unbroken too). Purely decorative/supplemental — the real accessible sequence is already
 * conveyed by each tile's own `position N of M` label and the live-region move announcement, so
 * this SVG is `aria-hidden`.
 *
 * Rendered as a DOM sibling BEFORE the tiles (not after, and with no explicit z-index) so normal
 * painting order alone puts the tiles on top — an arc's endpoints sit right at each tile's edge,
 * and having the tile visually "on top of" the arc's terminus reads as the arc connecting INTO the
 * tile, not stopping short of it. */
function OrderingCircleArcs({ n, rx, ry, width, height, dimFromIndex, direction }: { n: number; rx: number; ry: number; width: number; height: number; dimFromIndex: Set<number>; direction: OrderingCircleDirection }) {
  const markerId = useId();
  const cx = width / 2;
  const cy = height / 2;
  if (n < 2) return null;
  return (
    <svg
      aria-hidden='true'
      className='pointer-events-none absolute inset-0'
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
    >
      <defs>
        <marker id={markerId} viewBox='0 0 10 10' refX='7' refY='5' markerWidth='5' markerHeight='5' orient='auto-start-reverse'>
          <path d='M0,0 L10,5 L0,10 z' className='fill-ws-muted' />
        </marker>
      </defs>
      {Array.from({ length: n }, (_, i) => (
        <path
          key={i}
          d={ringArcPath(i, n, rx, ry, cx, cy, direction)}
          fill='none'
          className={`stroke-ws-muted transition-opacity ${dimFromIndex.has(i) ? 'opacity-20' : 'opacity-60'}`}
          strokeWidth={1.5}
          markerEnd={`url(#${markerId})`}
        />
      ))}
    </svg>
  );
}

interface OrderingCircleTileProps {
  id: string;
  item: OrderingCircleItem;
  disabled?: boolean;
  isOver: boolean;
  position: { x: number; y: number };
  ariaLabel?: string;
  /** Full plain-text label (no "position N of M" suffix) — shown as a native hover tooltip on the
   * text branch, since the tile's own visible text is truncated to fit a fixed-width tile. The
   * FULL text is already the tile's accessible name via `ariaLabel` (screen readers were never
   * affected by the visual truncation), but a sighted mouse user had no way to recover it — this
   * closes that gap for them specifically. */
  plainLabel: string;
  onArrowKey: (e: React.KeyboardEvent, id: string) => void;
}

/** The tile's visible content only (grip + text/media) — no positioning, no drag wiring. Shared
 * between the real in-place tile below and the floating DragOverlay clone, so the two can never
 * visually disagree about what a tile looks like. */
function OrderingCircleTileContent({ item, disabled, plainLabel }: { item: OrderingCircleItem; disabled?: boolean; plainLabel: string }) {
  return (
    <>
      {!disabled && <GripVertical className='h-3.5 w-3.5 shrink-0 text-ws-muted' aria-hidden='true' />}
      {item.media
        ? <OrderingItemMediaContent media={item.media} constrained ownerName={plainLabel} />
        : item.formulaChip
        ? <span className='min-w-0 flex-1 line-clamp-2 break-words'>{item.formulaChip}</span>
        // A tile's own height is driven purely by its content (tiles are individually absolutely-
        // positioned, not flex siblings that stretch to match a neighbor) — so a plain-text tile
        // is free to grow instead of staying pinned to one line. Clamped at 2 lines, not 3: the
        // clamp and TILE_HEIGHT_TEXT have to agree, and a text-only ring now reserves only the
        // 2-line height rather than a media tile's — which is what lets the ring shrink. Raising
        // this back to 3 means raising TILE_HEIGHT_TEXT to ~76 and accepting the larger ring.
        //
        // `<p>` wraps every RichTextEditor doc (even single-line ones), and a block-level child
        // defeats `line-clamp` the same way it defeats `truncate`'s ellipsis (both need the
        // overflowing text to be the box's OWN inline content, not a nested block's) — stripping
        // the wrapper tags fixes this the same way MCQ/Dropdown's own truncated option chips
        // already do (`.replace(/<\/?p>/g, '')`).
        : <PreviewHtml html={item.text.replace(/<\/?p>/g, '').trim()} as='span' className='min-w-0 flex-1 line-clamp-2 break-words' title={plainLabel || undefined} />}
    </>
  );
}

function OrderingCircleTile({ id, item, disabled, isOver, position, ariaLabel, plainLabel, onArrowKey }: OrderingCircleTileProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled, animateLayoutChanges: disableAnimateLayoutChanges });
  // CSS.Transform.toString returns undefined (not '') when there's no active drag transform —
  // string-interpolating that unguarded would inject the literal text "undefined" into the
  // transform value, making the whole property invalid and silently dropping the centering
  // translate too. With a DragOverlay in play (see OrderingCircle below), this in-place tile no
  // longer needs to visually follow the pointer at all — it just fades to indicate "this is what's
  // being moved" while the overlay clone does the actual following — but the transform is kept
  // (rather than dropped entirely) since dnd-kit still uses it to compute collision/over state.
  const dragTransform = CSS.Transform.toString(transform);
  const composedTransform = dragTransform ? `translate(-50%, -50%) ${dragTransform}` : 'translate(-50%, -50%)';

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      aria-label={disabled ? undefined : ariaLabel}
      aria-hidden={disabled ? true : undefined}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={disabled ? undefined : (e) => onArrowKey(e, id)}
      className={`absolute flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-medium focus-visible:[outline:2px_auto] ${
        disabled ? 'pointer-events-none cursor-default' : 'cursor-grab touch-none active:cursor-grabbing'
      } ${isDragging ? 'opacity-40' : ''} ${
        !disabled && isOver
          ? 'border-ws-primary border-dashed bg-ws-primary/10'
          : 'border-ws-border bg-ws-btn text-ws-text'
      }`}
      style={{
        width: TILE_WIDTH,
        left: `calc(50% + ${position.x}px)`,
        top: `calc(50% + ${position.y}px)`,
        transform: composedTransform,
        transition,
        zIndex: isDragging ? 20 : undefined,
      }}
    >
      <OrderingCircleTileContent item={item} disabled={disabled} plainLabel={plainLabel} />
    </div>
  );
}

export function OrderingCircle({ items, disabled, onChange, size = 260, direction = 'clockwise' }: OrderingCircleProps) {
  const { t } = useTranslation();
  const [order, setOrder] = useState(() => items.map(i => i.id));
  // Re-sync if the canonical item set itself changes shape (added/removed/reordered upstream)
  // while this widget is mounted — e.g. the teacher editing items with the live preview open.
  const itemsKey = items.map(i => i.id).join(',');
  useEffect(() => {
    setOrder(items.map(i => i.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey]);

  const [overId, setOverId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
  );

  const n = order.length;
  // `size` is a baseline/minimum, not a fixed value — for larger item counts the ring must grow
  // past it or tiles start overlapping (see minRadiusForCount's own doc comment).
  // Worst case across the items actually present, not across every item that could ever exist.
  const worstTileHeight = items.some(i => i.media) ? TILE_HEIGHT_MEDIA : TILE_HEIGHT_TEXT;
  const rx = Math.max((size - TILE_WIDTH) / 2, minRadiusForCount(n, worstTileHeight));
  const ry = rx * ELLIPSE_RATIO;
  // Padding trimmed 24 -> 12: it was symmetric slack around a square container, and on the vertical
  // axis it compounded exactly the gap this change exists to remove.
  const width = rx * 2 + TILE_WIDTH + 12;
  const height = ry * 2 + worstTileHeight + 12;
  const byId = new Map(items.map(i => [i.id, i]));
  /* Which tiles MOVE — asked of the assets, since an item stores only an id. See `pictureName`. */
  const animatedAssetIds = useAnimatedAssetIds(
    useMemo(() => items.flatMap(i => (i.media?.kind === 'image' ? [i.media.assetId] : [])), [items]),
  );

  /**
   * The item's readable text, or '' when it has none worth showing.
   *
   * A bare tag-strip is wrong here and was actively harmful: KaTeX renders a formula as MathML AND
   * a raw-TeX `<annotation>` AND the visual markup, so stripping tags concatenated all three —
   * "5 x + 4 = 39 5x+4=39 5 x + 4 = 39", and after reaction-splitting landed, a visible
   * "\\ce{2H2 +}\\allowbreak". That string was the hover tooltip, the tile's accessible name, and
   * what got SPOKEN on every keyboard drag.
   */
  /**
   * A MEDIA tile is named by its ALT TEXT, never by `text`.
   *
   * `text` on a media item is its internal label — storage, so a recorded answer can be read back
   * later — and `handleMediaSelect` seeds it from the asset's filename. Reading it here announced
   * the teacher's filing name to a listener and dropped the alt text, which is the one string
   * written FOR them. The linear shape had the identical fault; both now compose the name the same
   * way an MCQ media option does, motion text included. See `animatedAltText`.
   */
  function plainFor(id: string): string {
    const item = byId.get(id);
    if (item?.media?.kind === 'image') {
      return pictureName(item.media.altText, item.media.motionDescription,
        animatedAssetIds.has(item.media.assetId), t);
    }
    // Audio names itself by position only, deliberately and permanently — see MCQ's own
    // `mediaAccessibleName` for why a transcript specific enough to tell two clips apart is
    // specific enough to give away the answer.
    if (item?.media) return '';
    const text = item?.text;
    return text ? stripHtmlForTooltip(text).replace(/\s+/g, ' ').trim() : '';
  }

  /** The same text for places that must name the tile — a screen reader's announcement cannot be
   * blank — so a math-only item falls back to its position, exactly as MCQ's media options do. */
  function labelFor(id: string): string {
    const plain = plainFor(id);
    if (plain) return plain;
    const idx = items.findIndex(i => i.id === id);
    return t('blocks.ordering.itemFallbackLabel', { number: idx >= 0 ? idx + 1 : 1 });
  }

  /**
   * A tile's accessible name, stated as its NEIGHBOURS rather than its index.
   *
   * Circular grading is rotation-equivalent — `ordering.ts` says so outright, and the ring is drawn
   * as an unbroken loop for the same reason — so "position 2 of 4" announces the one fact that
   * carries no correctness information. Worse, it invites someone to hunt for a "position 1" the
   * shape does not have. What the evaluator actually compares is which item follows which, so that
   * is what the label says.
   *
   * "after/before", not "between": direction is load-bearing here (`circularDirection` decides which
   * way the sequence advances) and "between A and B" does not say which side is which. `order` is
   * already in graded index order, so its neighbours ARE the graded relationship — independent of
   * which way the ring happens to be drawn on screen.
   *
   * Two items make prev and next the same tile ("after A, before A" reads as a bug), and one item
   * has no neighbours at all; both get their own phrasing.
   */
  function tileName(id: string, index: number, seq: string[] = order): string {
    const item = labelFor(id);
    if (n < 2) return item;
    // A neighbour is CONTEXT, not the subject, so it is shortened. Left whole, one long item is
    // spoken in full three times over — once as itself and once inside each adjacent tile — which
    // buries the word that actually distinguishes them. The tile's own name is never truncated.
    const near = (i: number) => {
      const full = labelFor(seq[(i + n) % n]!);
      return full.length > NEIGHBOUR_NAME_MAX ? `${full.slice(0, NEIGHBOUR_NAME_MAX).trimEnd()}...` : full;
    };
    if (n === 2) return t('blocks.ordering.circleItemPair', { item, other: near(index - 1) });
    return t('blocks.ordering.circleItemNeighbours', { item, prev: near(index - 1), next: near(index + 1) });
  }

  function commitMove(from: number, to: number) {
    if (from === to) return;
    const next = arrayMove(order, from, to);
    setOrder(next);
    onChange?.(next);
    const movedId = order[from]!;
    // Reuses the tile's own phrasing rather than composing a second one. Two reasons: the moved
    // tile keeps focus, so a screen reader that re-reads a changed label would otherwise say the
    // neighbours twice in two different wordings; and the old announcement led with "position N of
    // M", the absolute index the region label now explicitly tells the user does not matter.
    setAnnouncement(t('blocks.ordering.circleMoveAnnouncement', { name: tileName(movedId, to, next) }));
  }

  function handleDragStart({ active }: DragStartEvent) {
    setActiveId(String(active.id));
  }

  function handleDragOver({ over }: DragOverEvent) {
    setOverId(over ? String(over.id) : null);
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    setOverId(null);
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const from = order.indexOf(String(active.id));
    const to = order.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    commitMove(from, to);
  }

  function handleDragCancel() {
    setOverId(null);
    setActiveId(null);
  }

  function handleArrowKey(e: React.KeyboardEvent, id: string) {
    const from = order.indexOf(id);
    const rtl = isRtl();
    const clockwiseKeys = rtl ? ['ArrowLeft', 'ArrowDown'] : ['ArrowRight', 'ArrowDown'];
    const counterClockwiseKeys = rtl ? ['ArrowRight', 'ArrowUp'] : ['ArrowLeft', 'ArrowUp'];
    // The physical key that FEELS like "move clockwise" should always visually move the tile
    // clockwise, regardless of which way `direction` renders increasing index — since mirroring
    // reverses the correspondence between index-direction and screen-direction (see ringPointAt's
    // own doc comment), the index delta a given key produces has to flip too, or a counterclockwise
    // ring would make the clockwise-feeling key visually move the tile backwards.
    const clockwiseDelta = direction === 'clockwise' ? 1 : -1;
    if (clockwiseKeys.includes(e.key)) {
      e.preventDefault();
      commitMove(from, (from + clockwiseDelta + n) % n);
    } else if (counterClockwiseKeys.includes(e.key)) {
      e.preventDefault();
      commitMove(from, (from - clockwiseDelta + n) % n);
    }
  }

  // Which two arcs (if any) touch the actively-dragged tile's REST slot — dimmed rather than
  // hidden outright while dragging, since the slot itself doesn't move until drop (only the
  // dragged tile's own floating clone does), so the connector is still geometrically accurate,
  // just momentarily less meaningful given the tile it touches has visually detached from that
  // spot. See this component's own DragOverlay comment for why the slot stays put mid-drag.
  const activeIndex = activeId ? order.indexOf(activeId) : -1;
  const dimFromIndex = activeIndex === -1 ? new Set<number>() : new Set([(activeIndex - 1 + n) % n, activeIndex]);

  return (
    <div className='flex flex-col items-center gap-2'>
      {/* The ring's diameter grows with item count (see minRadiusForCount) to keep tiles from
          overlapping — on a narrow phone viewport that can exceed the visible width well before
          it exceeds a reasonable item count. Rather than shrinking the ring (which would bring
          overlap back) or capping it outright (which would just clip tiles off-screen), this
          wrapper scrolls horizontally, the same "content wider than viewport" affordance the
          horizontal shape already gets for free from flex-wrap overflow. */}
      <div className='max-w-full overflow-x-auto'>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          accessibility={{ screenReaderInstructions: { draggable: t('blocks.ordering.circleDragInstructions') } }}
        >
          <SortableContext items={order} strategy={() => null}>
            <div
              role={disabled ? undefined : 'application'}
              aria-label={disabled ? undefined : t('blocks.ordering.circleRegionLabel', { total: n })}
              className='relative mx-auto shrink-0'
              style={{ width, height }}
            >
              <OrderingCircleArcs n={n} rx={rx} ry={ry} width={width} height={height} dimFromIndex={dimFromIndex} direction={direction} />
              {order.map((id, index) => {
                const item = byId.get(id);
                if (!item) return null;
                return (
                  <OrderingCircleTile
                    key={id}
                    id={id}
                    item={item}
                    disabled={disabled}
                    isOver={overId === id}
                    position={slotPosition(index, n, rx, ry, direction)}
                    ariaLabel={tileName(id, index)}
                    plainLabel={plainFor(id)}
                    onArrowKey={handleArrowKey}
                  />
                );
              })}
            </div>
          </SortableContext>
          {/* Rendering the actively-dragged tile's content here, in a portal-based floating clone
              that dnd-kit positions and moves to follow the pointer itself, is what stops the
              in-place tile from needing to visually follow the pointer via its own transform —
              and that transform is exactly where @dnd-kit/core's own `adjustScale` was stretching
              the tile to match whatever it hovered over (a `over.rect`-derived scaleX/scaleY term
              baked unconditionally into every active-drag transform whenever no DragOverlay is
              present — confirmed by direct measurement, not a strategy/animateLayoutChanges issue
              at all). No `restrict`/scale modifier can intercept this from the outside; using a
              DragOverlay is the one documented way dnd-kit itself skips computing it. */}
          <DragOverlay dropAnimation={null}>
            {activeId ? (() => {
              const activeItem = byId.get(activeId);
              if (!activeItem) return null;
              return (
                <div
                  className='flex items-center gap-1.5 rounded-md border border-ws-primary bg-ws-surface px-2 py-1.5 text-xs font-medium shadow-lg'
                  style={{ width: TILE_WIDTH, cursor: 'grabbing' }}
                >
                  <OrderingCircleTileContent item={activeItem} plainLabel={plainFor(activeId)} />
                </div>
              );
            })() : null}
          </DragOverlay>
        </DndContext>
      </div>
      {!disabled && <div aria-live='polite' aria-atomic='true' className='sr-only'>{announcement}</div>}
    </div>
  );
}
